import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { DrpLocatorAdapter } from '../../services/drp-locator-adapter';
import { DrpLocatorService } from '../../services/drp-locator.service';
import * as appStateActions from '../app-state/app-state.actions';
import { APP_STATE, MAP_TRIGGER } from '../app-state/app-state.models';
import * as geoCoderActions from '../geo-coder/geo-coder.actions';
import { State } from '../selectors';
import * as fromRoot from '../selectors';
import * as ServiceLocatorActions from './service-locator.actions';
import { ServiceLocatorActionTypes } from './service-locator.actions';
import { InitiateConfig, SearchContext, SearchResult } from './service-locator.models';

@Injectable()
export class ServiceLocatorEffects {
  constructor(
    private actions$: Actions,
    private drpLocator: DrpLocatorService,
    private store: Store<State>,
    private drpLocatorAdapter: DrpLocatorAdapter
  ) {}

  searchContext$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceLocatorActionTypes.SERVICE_SEARCH_INIT),
      map((action: ServiceLocatorActions.ServiceSearchInitAction) => action.payload),
      mergeMap((payload: InitiateConfig) => {
        const searchType =
          payload.searchContext === SearchContext.DRP_SHOP_SEARCH ? 'location' : 'autocomplete';
        return [
          new appStateActions.InitAppStateAction(),
          new geoCoderActions.InitGeoCoderStateAction(),
          new appStateActions.SetSearchTypeAction(searchType)
        ];
      })
    )
  );

  searching$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceLocatorActionTypes.SERVICE_SEARCH),
      map(() => new appStateActions.SetMapRedrawTriggerAction(MAP_TRIGGER.ON_SEARCH))
    )
  );

  // TODO: figure out why merge map wasn't working - want to combine the searchFail and the searchSuccess effects

  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceLocatorActionTypes.SERVICE_SEARCH),
      map((action: ServiceLocatorActions.ServiceSearchAction) => action.payload),
      withLatestFrom(
        this.store.pipe(select(fromRoot.getSearchContext)),
        this.store.pipe(select(fromRoot.getclaimVendorLocationUrl))
      ),
      switchMap(([payload, context, url]) =>
        this.drpLocator.getShops(payload, context, url).pipe(
          map(
            (modeledResponse: SearchResult[]) =>
              new ServiceLocatorActions.ServiceSearchSuccessAction(modeledResponse)
          ),
          catchError(error => of(new ServiceLocatorActions.ServiceSearchFailAction(error)))
        )
      )
    )
  );

  searchFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceLocatorActionTypes.SERVICE_SEARCH_FAIL),
      map((action: ServiceLocatorActions.ServiceSearchFailAction) => action.payload),
      withLatestFrom(this.store.select(fromRoot.getCurrentAppState)),
      map(([payload, currentAppState]) => {
        if (
          currentAppState === APP_STATE.NO_ZIP_FOUND ||
          currentAppState === APP_STATE.OUT_OF_COUNTRY
        ) {
          return new appStateActions.SetMapRedrawTriggerAction(MAP_TRIGGER.ON_ERROR);
        } else {
          return new appStateActions.SetAppStateAction(APP_STATE.RESULTS_NOT_FOUND);
        }
      })
    )
  );

  searchSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceLocatorActionTypes.SERVICE_SEARCH_SUCCESS),
      map((action: ServiceLocatorActions.ServiceSearchSuccessAction) => action.payload),
      map(() => new appStateActions.SetAppStateAction(APP_STATE.RESULTS_FOUND))
    )
  );
}
